// Typed API client — fetch wrapper with auth headers and error handling
import { getAccessToken } from "./auth";
import type { ApiError, PaginatedResponse } from "@/types";

const BASE_URL = "/api";

function buildApiUrl(path: string): string {
  if (path === BASE_URL || path.startsWith(`${BASE_URL}/`)) {
    return path;
  }

  return `${BASE_URL}${path.startsWith("/") ? path : `/${path}`}`;
}

export class ApiClient {
  private async getHeaders(): Promise<HeadersInit> {
    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };
    // In dev mode, pass the dev token as Bearer for API auth middleware
    const token = await getAccessToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    // In production, SWA automatically forwards auth cookies — no Bearer needed
    return headers;
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    query?: Record<string, string | number | undefined>
  ): Promise<T> {
    const headers = await this.getHeaders();
    let url = buildApiUrl(path);

    if (query) {
      const params = new URLSearchParams();
      Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, String(value));
        }
      });
      const qs = params.toString();
      if (qs) url += `?${qs}`;
    }

    const response = await fetch(url, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      const apiError: ApiError = errorBody || {
        error: { code: "UNKNOWN", message: `HTTP ${response.status}` },
      };
      throw new ApiRequestError(response.status, apiError);
    }

    if (response.status === 204) return undefined as T;
    const text = await response.text();
    if (!text) return undefined as T;
    return JSON.parse(text);
  }

  async get<T>(path: string, query?: Record<string, string | number | undefined>): Promise<T> {
    return this.request<T>("GET", path, undefined, query);
  }

  async post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>("POST", path, body);
  }

  async patch<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>("PATCH", path, body);
  }

  async delete<T>(path: string): Promise<T> {
    return this.request<T>("DELETE", path);
  }
}

export class ApiRequestError extends Error {
  constructor(
    public status: number,
    public body: ApiError
  ) {
    super(body.error.message);
    this.name = "ApiRequestError";
  }
}

export function getApiErrorMessage(error: unknown): string {
  if (!(error instanceof ApiRequestError)) {
    return error instanceof Error ? error.message : "Unexpected API error";
  }

  const details = error.body.error.details ?? [];
  const detailValue = (field: string) => details.find((detail) => detail.field === field)?.issue;

  if (error.body.error.code === "API_PROXY_UNAVAILABLE") {
    const endpoint = detailValue("path");
    const target = detailValue("target");
    const requestId = detailValue("request_id");
    const pieces = [error.body.error.message];

    if (endpoint) pieces.push(`Endpoint: ${endpoint}.`);
    if (target) pieces.push(`Check that the local API host is running on ${target}.`);
    if (requestId) pieces.push(`Reference: ${requestId}.`);

    return pieces.join(" ");
  }

  if (error.status >= 500) {
    return `Something went wrong while contacting the API. Status: ${error.status}.`;
  }

  return error.message;
}

export const api = new ApiClient();
